import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCreateVisita, useUpdateVisita } from '@offertify/query';
import toast from 'react-hot-toast';
import { Loader2, AlertCircle, X } from 'lucide-react';
import { leadsAPI } from '../../utils/api';
import { useActivityTypes, resolveActivityType } from '../../hooks/useActivityTypes';
import RecurrenceFields, { useRecurrence } from './RecurrenceFields';
import RecurrenceDelete from './RecurrenceDelete';
import AttivitaAllegati from './AttivitaAllegati';
import type { Visita, VisitaStato } from '../../types';

export type AttivitaContext =
  | { kind: 'lead'; id: string }
  | { kind: 'cliente'; id: string }
  | { kind: 'offerta'; id: string };

interface AttivitaFormProps {
  context: AttivitaContext;
  /** Presente solo in modifica (contesto cliente/offerta — le attività lead sono un event-log, non modificabile). */
  visita?: Visita | null;
  /** Contesto lead: form sempre visibile, senza chiusura. Cliente/offerta: pannello dismissibile. */
  onClose?: () => void;
  onSuccess: () => void;
}

/** Selettore tipo con icona/colore dall'anagrafica, condiviso dai due rami del form. */
function TipoPicker({
  value,
  onChange,
  types,
}: {
  value: string;
  onChange: (codice: string) => void;
  types: ReturnType<typeof useActivityTypes>['data'];
}) {
  const selectable = (types || []).filter((t) => !t.is_system);
  return (
    <div className="flex gap-2 flex-wrap">
      {selectable.map((t) => {
        const resolved = resolveActivityType(t.codice, types);
        const Icon = resolved.icon;
        const isActive = value === t.codice;
        return (
          <button
            key={t.codice}
            type="button"
            onClick={() => onChange(t.codice)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors"
            style={
              isActive
                ? { backgroundColor: resolved.colore, borderColor: resolved.colore, color: '#fff' }
                : { borderColor: '#d1d5db', color: '#374151' }
            }
          >
            <Icon className="h-3.5 w-3.5" />
            {resolved.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AttivitaForm({ context, visita, onClose, onSuccess }: AttivitaFormProps) {
  const { data: types } = useActivityTypes(context.kind);

  if (context.kind === 'lead') {
    return <LeadAttivitaForm leadId={context.id} types={types} onSuccess={onSuccess} />;
  }

  return (
    <VisitaAttivitaForm
      context={context}
      visita={visita || null}
      types={types}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
}

// --- Lead: event-log (subject + body) ---
function LeadAttivitaForm({
  leadId,
  types,
  onSuccess,
}: {
  leadId: string;
  types: ReturnType<typeof useActivityTypes>['data'];
  onSuccess: () => void;
}) {
  const queryClient = useQueryClient();
  const defaultType = (types || []).find((t) => !t.is_system && t.codice === 'nota')?.codice
    || (types || []).find((t) => !t.is_system)?.codice
    || 'nota';
  const [type, setType] = useState(defaultType);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      leadsAPI.createActivity(leadId, {
        type,
        subject: subject || undefined,
        body: body || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      toast.success('Attività registrata');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    },
  });

  const canSubmit = (subject.trim() || body.trim()) && !createMutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) createMutation.mutate();
      }}
      className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
    >
      <TipoPicker value={type} onChange={setType} types={types} />
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Oggetto (es. Follow-up demo prodotto)"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Dettagli (opzionale)"
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
      />
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Registra
        </button>
      </div>
    </form>
  );
}

// --- Cliente/Offerta: appuntamento (data/ora/indirizzo/note + stato/esito/follow-up in edit) ---
function VisitaAttivitaForm({
  context,
  visita,
  types,
  onClose,
  onSuccess,
}: {
  context: { kind: 'cliente' | 'offerta'; id: string };
  visita: Visita | null;
  types: ReturnType<typeof useActivityTypes>['data'];
  onClose?: () => void;
  onSuccess: () => void;
}) {
  const isEdit = !!visita;
  const recurrence = useRecurrence(visita);
  const defaultTipo = (types || []).find((t) => !t.is_system && t.codice === 'visita')?.codice
    || (types || []).find((t) => !t.is_system)?.codice
    || 'visita';
  const [tipo, setTipo] = useState(visita?.tipo || defaultTipo);
  const [data, setData] = useState(visita?.data || '');
  const [ora, setOra] = useState(visita?.ora?.substring(0, 5) || '');
  const [note, setNote] = useState(visita?.note || '');
  const [indirizzo, setIndirizzo] = useState(visita?.indirizzo || '');
  const [stato, setStato] = useState<VisitaStato>(visita?.stato || 'programmata');
  const [esito, setEsito] = useState(visita?.esito || '');
  const [followUpDate, setFollowUpDate] = useState(visita?.follow_up_date || '');
  const [followUpNote, setFollowUpNote] = useState(visita?.follow_up_note || '');
  const [error, setError] = useState('');

  const createVisita = useCreateVisita();
  const updateVisita = useUpdateVisita();
  const isSaving = createVisita.isPending || updateVisita.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) {
      setError('La data è obbligatoria');
      return;
    }
    setError('');
    try {
      if (isEdit && visita) {
        await updateVisita.mutateAsync({
          id: visita.id,
          data: {
            tipo,
            data: data || undefined,
            ora: ora || undefined,
            note: note || undefined,
            indirizzo: indirizzo || undefined,
            scope: recurrence.scope,
            ...(recurrence.scope === 'singola' ? {
              stato, esito: esito || undefined, follow_up_date: followUpDate || undefined,
              follow_up_note: followUpNote || undefined,
            } : {}),
          },
        });
      } else {
        await createVisita.mutateAsync({
          ricorrenza: recurrence.rule,
          [context.kind === 'cliente' ? 'cliente_id' : 'offerta_id']: context.id,
          data,
          ora: ora || undefined,
          tipo,
          note: note || undefined,
          indirizzo: indirizzo || undefined,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  return (
    <div className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">
          {isEdit ? 'Modifica attività' : 'Nuova attività'}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
          <TipoPicker value={tipo} onChange={setTipo} types={types} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Data *</label>
            <input
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ora</label>
            <input
              type="time"
              value={ora}
              onChange={(e) => setOra(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <RecurrenceFields value={recurrence} start={data} />
        {visita && <RecurrenceDelete visita={visita} onSuccess={onSuccess} />}
        {/* Edit mode: stato + esito */}
        {isEdit && recurrence.scope === 'singola' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Stato</label>
              <select
                value={stato}
                onChange={(e) => setStato(e.target.value as VisitaStato)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="programmata">Programmata</option>
                <option value="completata">Completata</option>
                <option value="annullata">Annullata</option>
                <option value="rinviata">Rinviata</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Esito</label>
              <input
                type="text"
                value={esito}
                onChange={(e) => setEsito(e.target.value)}
                placeholder="Esito della visita/chiamata..."
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Indirizzo (solo per visita) */}
        {tipo === 'visita' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Indirizzo</label>
            <input
              type="text"
              value={indirizzo}
              onChange={(e) => setIndirizzo(e.target.value)}
              placeholder="Indirizzo della visita (opzionale, usa quello del cliente se vuoto)"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Note</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Note pre-visita, argomenti da trattare..."
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </div>

        {/* Follow-up (edit mode) */}
        {isEdit && recurrence.scope === 'singola' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Data follow-up</label>
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Note follow-up</label>
              <input
                type="text"
                value={followUpNote}
                onChange={(e) => setFollowUpNote(e.target.value)}
                placeholder="Note per il follow-up..."
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Allegati: caricati subito (non con "Salva"), serve un'attività già esistente */}
        {isEdit && visita && (
          <div className="border-t border-gray-100 pt-4">
            <AttivitaAllegati visitaId={visita.id} />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Salva modifiche' : 'Crea attività'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
}
